import React from 'react';

import { ArrowRightIcon, PlayArrowIcon } from '@change/ds-icons';

export type VideoPreviewButtonProps = {
	/** Thumbnail image URL for the preview popover */
	thumbnailUrl: string;
	/** Alt text for the thumbnail image */
	thumbnailAlt: string;
	/** Accessible label for the button */
	ariaLabel: string;
	/** Called when the user focuses or hovers (preview is shown) */
	onAccess: () => void;
	/** Called when the user blurs or mouse leaves (preview is hidden) */
	onBlur: () => void;
	/** Optional. Called when the button is clicked (e.g. to scroll to the featured videos section) */
	onClick?: () => void;
	/** Optional prefix for data-testid attributes (e.g. "momentum-bar" → momentum-bar-video-preview) */
	testIdPrefix?: string;
};

export const VideoPreviewButton: React.FC<VideoPreviewButtonProps> = ({
	thumbnailUrl,
	thumbnailAlt,
	ariaLabel,
	onAccess,
	onBlur,
	onClick,
	testIdPrefix = 'momentum-bar',
}) => {
	return (
		<button
			type="button"
			className="group relative flex shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#403A49] focus-visible:ring-offset-2"
			data-testid={`${testIdPrefix}-video-preview`}
			aria-label={ariaLabel}
			onClick={onClick}
			onFocus={onAccess}
			onBlur={onBlur}
			onMouseEnter={onAccess}
			onMouseLeave={onBlur}
		>
			<div className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-2 border-[#403A49]">
				<PlayArrowIcon size="x-small" className="text-[#403A49]" data-testid={`${testIdPrefix}-video-icon`} />
			</div>

			<div className="absolute bottom-full left-1/2 z-50 hidden -translate-x-1/2 transform cursor-pointer pb-2 group-focus-within:block group-hover:block">
				<div className="relative h-[210px] w-[140px] overflow-hidden rounded-lg shadow-lg">
					<img
						src={thumbnailUrl}
						alt={thumbnailAlt}
						draggable={false}
						className="h-full w-full object-cover"
						data-testid={`${testIdPrefix}-video-thumbnail`}
					/>

					<div className="absolute right-3 bottom-3 flex h-6 w-6 items-center justify-center rounded-xl bg-[#FFE600]">
						<ArrowRightIcon
							size="x-small"
							className="text-black"
							data-testid={`${testIdPrefix}-video-thumbnail-play-icon`}
						/>
					</div>
				</div>
			</div>
		</button>
	);
};
